<script setup>
import { computed, ref, onUnmounted, onMounted } from 'vue'
import { useCartStore } from '../stores/cartStore'
import CartPaymentOrderSummaryAccordion from './CartPaymentOrderSummaryAccordion.vue'

const cartStore = useCartStore()

const stateFee = computed(() => {
  if (cartStore.products.length === 0) return undefined

  const product = cartStore.products.find((product) =>
    product.name.includes('State Filing Fee')
  )
  return product ? product : undefined
})

const ccProduct = computed(() => {
  if (cartStore.products.length === 0) return undefined
  return cartStore.products.filter(
    (product) => !product.name.includes('State Filing Fee')
  )[0]
})

// Target time in minutes
const targetMinutes = 25 // for example, 1 minute from now
const endTime = new Date(new Date().getTime() + targetMinutes * 60000)

// Countdown value in seconds
const countdown = ref((endTime - new Date()) / 1000)

// Update the countdown every second
const updateCountdown = () => {
  const secondsLeft = (endTime - new Date()) / 1000
  countdown.value = secondsLeft > 0 ? secondsLeft : 0
}

let intervalId = null

onMounted(() => {
  intervalId = setInterval(updateCountdown, 1000)
})

onUnmounted(() => {
  clearInterval(intervalId)
})

// Convert seconds to minutes for display
const displayMinutes = () => {
  const minutes = Math.floor(countdown.value / 60)
  const seconds = Math.floor(countdown.value % 60)
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
}
</script>
<template>
  <div class="ps-lg-2 px-auto">
    <div class="card card-sm shadow-none mb-4 border">
      <div class="card-body">
        <div class="border-bottom pb-4 mb-4">
          <h3 class="card-header-title">Order summary</h3>
        </div>

        <template
          v-if="
            cartStore.discount.amount > 0 || cartStore.discount.percent_off > 0
          "
        >
          <!-- Subtotal if Discount Present -->
          <div class="border-bottom mb-4">
            <div class="d-grid">
              <dl class="row mb-0">
                <dt class="col-8">Item Subtotal</dt>
                <dd class="col-4 text-sm-end mb-0">
                  ${{ cartStore.totalPriceNoDiscount }}
                </dd>
              </dl>
              <div class="d-grid">
                <div class="col text-body">
                  <CartPaymentOrderSummaryAccordion
                    anchorText="Order Details"
                  />
                </div>
              </div>
              <!-- End Row -->
            </div>
          </div>
          <!-- End Total -->
          <!-- Discount -->
          <div class="border-bottom pb-4 mb-4">
            <div class="d-flex">
              <div class="flex-shrink-0"></div>

              <div class="flex-grow-1">
                <dl class="row">
                  <dt class="col-8">Discount</dt>
                  <dd class="col-4 text-sm-end mb-0 float-end">
                    <template v-if="cartStore.discount.amount > 0">
                      (${{ cartStore.discount.amount / 100 }})
                    </template>
                    <template v-else-if="cartStore.discount.percent_off > 0">
                      {{ cartStore.discount.percent_off }}% off*
                    </template>
                  </dd>
                </dl>
                <div class="d-grid">
                  <div class="text-body">
                    <span class="small"
                      >{{ cartStore.discount.description }}
                    </span>
                    <template v-if="cartStore.discount.percent_off > 0">
                      <small style="font-style: italic; display: block"
                        >*% discounts do not apply to state fees</small
                      >
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- End Discount -->
        </template>
        <!-- End conditional Subtotal for Discount -->
        <!-- Due Today -->
        <div class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <dt class="mb-0">Due Today</dt>
            <dd class="mb-0">
              <strong>${{ cartStore.totalPrice.toFixed(2) }}</strong>
            </dd>
          </div>
          <div class="d-flex flex-column">
            <div class="text-body small mb-2" v-if="ccProduct">
              <span>Next Year: ${{ ccProduct.price / 100 }} + state fees</span>
            </div>
            <div
              v-if="
                cartStore.discount.amount == 0 ||
                cartStore.discount.percent_off == 0
              "
              class="text-body"
            >
              <CartPaymentOrderSummaryAccordion
                anchorText="View Order Details"
              />
            </div>
          </div>
        </div>
        <!-- End Due Today -->
      </div>
      <!-- End Card Body -->
    </div>
    <!-- End Card -->
  </div>
</template>
